import { heatmapTone, type HeatmapResult, type HeatmapTone } from './serp-heatmap.js';
import { volumeWeight, type QueryVolume } from './query-volume.js';
import { queryPositionMetrics } from './serp-tile-metrics.js';

export interface TreemapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Insets may consume a tiny cell, but must never grow it or cross its neighbours.
export function insetTreemapRect(rect: TreemapRect, gap = 3): TreemapRect {
  return {
    x: rect.x,
    y: rect.y,
    width: Math.max(0, rect.width - gap),
    height: Math.max(0, rect.height - gap),
  };
}

// A measured zero sorts before unknown demand. Manual priority does not override demand.
export function compareQueryDemand(
  a: { query: string; volume?: QueryVolume },
  b: { query: string; volume?: QueryVolume },
) {
  const known = (v?: QueryVolume) =>
    Number(v?.status === 'complete' && v.value !== null && !v.weightUnavailableReason);
  return (
    volumeWeight(b.volume) - volumeWeight(a.volume) ||
    known(b.volume) - known(a.volume) ||
    a.query.localeCompare(b.query, 'ru')
  );
}

export const QUERY_TILE_LEGEND = [
  ['fake', 'Фейк'],
  ['competitor', 'Конкурент'],
  ['unreviewed', 'Не разобрано в топ-3'],
  ['owned', 'Наши позиции'],
  ['conflict', 'Конфликт решения'],
  ['unknown', 'Неизвестно в топ-3'],
  ['external', 'Наших нет'],
  ['unavailable', 'Нет полных данных'],
] as const;
// Callers supply positive measured weights, or explicit count weights when unavailable.
export function treemapRects(
  weights: number[],
  x = 0,
  y = 0,
  width = 100,
  height = 100,
): TreemapRect[] {
  if (!weights.length) return [];
  if (weights.some((weight) => !Number.isFinite(weight) || weight <= 0))
    throw new Error('Площадь плитки требует положительного конечного веса');
  if (![x, y, width, height].every(Number.isFinite) || width < 0 || height < 0)
    throw new Error('Некорректные границы карты запросов');
  if (weights.length === 1) return [{ x, y, width, height }];
  const total = weights.reduce((a, b) => a + b, 0);
  if (!Number.isFinite(total)) throw new Error('Суммарный вес карты превышает допустимый предел');
  let split = 1,
    sum = weights[0]!;
  while (
    split < weights.length - 1 &&
    Math.abs(sum + weights[split]! - total / 2) < Math.abs(sum - total / 2)
  )
    sum += weights[split++]!;
  const fraction = sum / total;
  return width >= height
    ? [
        ...treemapRects(weights.slice(0, split), x, y, width * fraction, height),
        ...treemapRects(
          weights.slice(split),
          x + width * fraction,
          y,
          width * (1 - fraction),
          height,
        ),
      ]
    : [
        ...treemapRects(weights.slice(0, split), x, y, width, height * fraction),
        ...treemapRects(
          weights.slice(split),
          x,
          y + height * fraction,
          width,
          height * (1 - fraction),
        ),
      ];
}
export function queryPositionCounts(results: HeatmapResult[], top: number) {
  const metrics = queryPositionMetrics(results, top);
  return { ownedCount: metrics.owned.count, reviewCount: metrics.review.count };
}
export function queryTile(
  status: string,
  results: HeatmapResult[],
  top: number,
): { tone: HeatmapTone; label: string; strength: number } {
  if (status !== 'complete')
    return { tone: 'unavailable', label: 'Нет полных данных', strength: 1 };
  const current = results.filter((r) => r.rank >= 1 && r.rank <= top);
  const best = (tone: HeatmapTone) =>
    Math.min(...current.filter((r) => heatmapTone(r) === tone).map((r) => r.rank));
  const fake = best('fake');
  if (fake !== Infinity)
    return { tone: 'fake', label: `Фейк #${fake}`, strength: fake <= 3 ? 1 : 0.6 };
  for (const tone of ['conflict', 'unreviewed', 'unknown'] as const) {
    if (best(tone) <= 3)
      return {
        tone,
        label: `${tone === 'conflict' ? 'Конфликт решения' : tone === 'unknown' ? 'Неизвестно' : 'Не разобрано'} #${best(tone)}`,
        strength: 0.8,
      };
  }
  const competitor = best('competitor');
  if (competitor !== Infinity)
    return {
      tone: 'competitor',
      label: `Конкурент #${competitor}`,
      strength: competitor <= 3 ? 0.9 : 0.6,
    };
  const { ownedCount: owned, reviewCount: review } = queryPositionCounts(current, top);
  if (owned)
    return {
      tone: 'owned',
      label: `Наши: ${owned} из ${top} позиций · лучшая #${best('owned')}${review ? ` · требуют разбора: ${review}` : ''}`,
      strength: 0.25 + (0.75 * owned) / top,
    };
  return {
    tone: 'external',
    label: `Наших нет в Top-${top} · подтверждённых фейков нет`,
    strength: 0.4,
  };
}
